import React from 'react';
import { Card, Row, Col, Tag, Divider, Collapse } from 'antd';
import '../../../../assets/scss/components/OfflineBooking/SpaceInfoCard.scss';

const { Panel } = Collapse;

const SpaceInfoCard = ({ 
  spaceDetails, 
  availableChargingTypes = [], 
  selectedChargingOption 
}) => {
  if (!spaceDetails) return null;

  const getCapacity = () => {
    const capacity = 
      spaceDetails.spaceArea?.capacity || 
      spaceDetails.seatArrangements?.[0]?.max || 
      spaceDetails.events?.[0]?.seatArrangements?.[0]?.max ||
      spaceDetails.capacity;
    
    if (!capacity || capacity === '-') return '-';
    
    const isSeats = spaceDetails.seatArrangements?.[0]?.max || spaceDetails.events?.[0]?.seatArrangements?.[0]?.max;
    const unit = isSeats ? 'seats' : 'guests';
    
    return `${capacity} ${unit}`;
  };

  return (
    <div className="space-info-card">
      <Divider orientation="left">Space Information</Divider>
      
      <Collapse 
        defaultActiveKey={[]} 
        className="space-info-collapse"
      >
        <Panel header="View Space Details" key="1">
          <Card size="small" className="info-card">
        <Row gutter={24}>
          <Col span={8}>
            <div className="info-item">
              <strong>Space Name:</strong> {spaceDetails.spaceName || spaceDetails.name || spaceDetails.title}
            </div>
            <div className="info-item">
              <strong>Category:</strong> {spaceDetails.events?.[0]?.eventType?.spaceCategory?.name || 
                                          spaceDetails.eventTypes?.[0]?.spaceCategory?.name || '-'}
            </div>
            <div className="info-item">
              <strong>Event Type:</strong> {spaceDetails.events?.[0]?.eventType?.name || 
                                            spaceDetails.eventTypes?.[0]?.name || '-'}
            </div>
            <div className="info-item">
              <strong>Location:</strong> {spaceDetails.location?.address || '-'}
            </div>
          </Col>

          <Col span={8}>
            <div className="info-item">
              <strong>Capacity:</strong> {getCapacity()}
            </div>
            <div className="info-item">
              <strong>Available Charging Types:</strong> {availableChargingTypes.length > 0 
                ? availableChargingTypes.map(c => c.name).join(', ') 
                : '-'}
            </div>
            {selectedChargingOption && (
              <div className="info-item">
                <strong>Selected Rate:</strong> LKR {
                  selectedChargingOption.hourlyRate || 
                  selectedChargingOption.dailyRate || 
                  selectedChargingOption.weeklyRate || 
                  selectedChargingOption.monthlyRate || 
                  selectedChargingOption.blockRate || '-'
                }/{selectedChargingOption.type}
              </div>
            )}
          </Col>

          <Col span={8}>
            {selectedChargingOption && (
              <>
                <div className="info-item">
                  <strong>Min Booking:</strong> {selectedChargingOption.minimumBookingHours || 
                                                  selectedChargingOption.minimumBookingPeriod || '-'} {selectedChargingOption.unit || 'hours'}
                </div>
                {selectedChargingOption.acceptedHalfHourBooking && (
                  <div className="info-item">
                    <strong>Half Hour Booking:</strong> Accepted
                  </div>
                )}
                {selectedChargingOption.blocks && Array.isArray(selectedChargingOption.blocks) && selectedChargingOption.blocks.length > 0 && (
                  <div className="info-item">
                    <strong>Available Blocks:</strong> {selectedChargingOption.blocks.length}
                  </div>
                )}
              </>
            )}
            <div className="info-item">
              <strong>Host:</strong> {spaceDetails.spaceOwner?.firstName && spaceDetails.spaceOwner?.lastName 
                ? spaceDetails.spaceOwner.firstName + ' ' + spaceDetails.spaceOwner.lastName 
                : spaceDetails.spaceOwner?.email || '-'}
            </div>
          </Col>
        </Row>

        {(spaceDetails.events?.[0]?.amenities?.length > 0 || spaceDetails.amenities?.length > 0) && (
          <Row style={{ marginTop: 8 }}>
            <Col span={24}>
              <div className="info-item">
                <strong>Amenities:</strong> {((spaceDetails.events?.[0]?.amenities || spaceDetails.amenities) || [])
                  .map(a => a.name || a).join(', ')}
              </div>
            </Col>
          </Row>
        )}

        {spaceDetails.location && (
          <Row style={{ marginTop: 8 }}>
            <Col span={24}>
              <div className="info-item">
                <strong>City:</strong> {spaceDetails.location.city || '-'}
              </div>
              <div className="info-item">
                <strong>Country:</strong> {spaceDetails.location.country || '-'}
              </div>
            </Col>
          </Row>
        )}
      </Card>

      {/* Operating Hours and Additional Info */}
      {selectedChargingOption && (
        <Card size="small" className="availability-card">
          <Row gutter={24}>
            <Col span={8}>
              <div><strong>Operating Hours:</strong></div>
              <div>
                {(() => {
                  // Try to get operating hours from charging option first
                  const pricingOption = selectedChargingOption.pricingOptions?.[0];
                  if (pricingOption?.startTime && pricingOption?.endTime) {
                    return `${pricingOption.startTime} - ${pricingOption.endTime}`;
                  }
                  
                  // Try periodicalhours
                  const periodicalhours = spaceDetails.periodicalhours?.[0];
                  if (periodicalhours?.from && periodicalhours?.to) {
                    return `${periodicalhours.from} - ${periodicalhours.to}`;
                  }
                  
                  // Fallback to operatingHours
                  if (spaceDetails.operatingHours?.start && spaceDetails.operatingHours?.end) {
                    return `${spaceDetails.operatingHours.start} - ${spaceDetails.operatingHours.end}`;
                  }
                  
                  // Default
                  return '09:00 - 18:00';
                })()}
              </div>
            </Col>
            <Col span={8}>
              <div><strong>Minimum Booking:</strong></div>
              <div>{selectedChargingOption.minimumBookingHours || selectedChargingOption.minimumBookingPeriod || '-'} {selectedChargingOption.unit || 'hours'}</div>
              {selectedChargingOption.acceptedHalfHourBooking && (
                <div className="half-hour-accepted">✓ Half-hour booking accepted</div>
              )}
            </Col>
            <Col span={8}>
              {selectedChargingOption.type === 'block' && selectedChargingOption.blocks?.length > 0 && (
                <>
                  <div><strong>Available Blocks:</strong></div>
                  <div>{selectedChargingOption.blocks.length} time blocks configured</div>
                </>
              )}
              {selectedChargingOption.pricingOptions?.length > 0 && (
                <>
                  <div><strong>Pricing Variations:</strong></div>
                  <div>{selectedChargingOption.pricingOptions.length} price options</div>
                </>
              )}
            </Col>
          </Row>
          {spaceDetails.events?.[0]?.bufferPeriod && (
            <Row style={{ marginTop: 12 }}>
              <Col span={24}>
                <div><strong>Buffer Period:</strong> {spaceDetails.events[0].bufferPeriod.amount || 
                      spaceDetails.events[0].bufferPeriod.duration || '-'} {spaceDetails.events[0].bufferPeriod.unit || 'minutes'}</div>
              </Col>
            </Row>
          )}
          {selectedChargingOption.type === 'block' && selectedChargingOption.blocks?.length > 0 && (
            <Row style={{ marginTop: 12 }}>
              <Col span={24}>
                <div><strong>Block Details:</strong></div>
                <div className="block-tags">
                  {selectedChargingOption.blocks.map((block, idx) => (
                    <Tag key={idx} color="green">
                      {block.name || `Block ${idx + 1}`}: {block.startTime} - {block.endTime}
                    </Tag>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Card>
      )}
        </Panel>
      </Collapse>
    </div>
  );
};

export default SpaceInfoCard;
